import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Video, Play, MoreVertical, Pencil, Trash2, Eye, EyeOff,
  Lock, Image, Upload, ExternalLink, BarChart3, Heart, X,
} from 'lucide-react';
import { toast } from 'sonner';


/* ─── Video Edit Modal ───────────────────────────────────── */
function VideoEditModal({ video, open, onClose, onSaved, getAuthHeaders }) {
  const [form, setForm] = useState({
    title: video?.title || '',
    description: video?.description || '',
    visibility: video?.visibility || 'public',
    thumbnail_url: video?.thumbnail_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [thumbPreview, setThumbPreview] = useState(video?.thumbnail_url || '');

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.url || data.file_url;
        setForm(prev => ({ ...prev, thumbnail_url: url }));
        setThumbPreview(URL.createObjectURL(file));
        toast.success('Thumbnail uploaded');
      }
    } catch {
      toast.error('Failed to upload thumbnail');
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/videos/${video.video_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success('Video updated');
        onSaved?.();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch {
      toast.error('Failed to update video');
    }
    setSaving(false);
  };

  const VISIBILITY_OPTIONS = [
    { value: 'public', label: 'Public', icon: Eye, desc: 'Anyone can view' },
    { value: 'private', label: 'Private', icon: EyeOff, desc: 'Only you can view' },
    { value: 'subscribers', label: 'Subscribers Only', icon: Lock, desc: 'Subscribers only' },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="video-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Edit Video
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Thumbnail Preview + Upload */}
          <div>
            <Label className="text-xs font-semibold mb-1.5 block">Thumbnail</Label>
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted/30 border border-border group">
              {(thumbPreview || form.thumbnail_url) ? (
                <img
                  src={thumbPreview || form.thumbnail_url}
                  alt="Thumbnail"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                  <Image className="w-8 h-8 mb-1" />
                  <span className="text-xs">No thumbnail</span>
                </div>
              )}
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                </div>
              </div>
              {/* Upload overlay */}
              <label className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                <div className="flex items-center gap-2 bg-background/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-border text-xs font-medium">
                  <Upload className="w-3.5 h-3.5" /> Change Thumbnail
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                  data-testid="video-thumb-upload"
                />
              </label>
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="video-title" className="text-xs font-semibold">Title</Label>
            <Input
              id="video-title"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Video title"
              className="mt-1"
              data-testid="video-edit-title"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="video-desc" className="text-xs font-semibold">Description</Label>
            <Textarea
              id="video-desc"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add a description..."
              rows={3}
              className="mt-1 resize-none"
              data-testid="video-edit-description"
            />
          </div>

          {/* Visibility */}
          <div>
            <Label className="text-xs font-semibold mb-1.5 block">Visibility</Label>
            <div className="grid grid-cols-3 gap-2">
              {VISIBILITY_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const active = form.visibility === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setForm(prev => ({ ...prev, visibility: opt.value }))}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border text-center transition-all ${
                      active
                        ? 'border-secondary bg-secondary/10 text-foreground'
                        : 'border-border hover:border-muted-foreground/30 text-muted-foreground'
                    }`}
                    data-testid={`video-vis-${opt.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[11px] font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid="video-edit-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Video Card ─────────────────────────────────────────── */
function VideoCard({ video, onEdit, onDelete }) {
  const vis = video.visibility || 'public';
  const VisIcon = vis === 'private' ? EyeOff : vis === 'subscribers' ? Lock : Eye;

  return (
    <div className="group rounded-xl border border-border overflow-hidden bg-card/50 hover:bg-muted/20 transition-all" data-testid={`video-card-${video.video_id}`}>
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted/30">
        {video.thumbnail_url ? (
          <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/60 to-muted/20">
            <Video className="w-10 h-10 text-muted-foreground/40" />
          </div>
        )}
        {/* Play Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
          </div>
        </div>
        {/* Duration badge */}
        {video.duration && (
          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
            {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
          </span>
        )}
        {/* Visibility badge */}
        {vis !== 'public' && (
          <Badge className="absolute top-2 left-2 bg-black/60 text-white text-[10px] gap-1">
            <VisIcon className="w-3 h-3" /> {vis === 'private' ? 'Private' : 'Subs Only'}
          </Badge>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">{video.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{video.artist_name}</p>
          </div>
          {/* ••• Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-muted/50 text-muted-foreground" data-testid={`video-menu-${video.video_id}`}>
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={() => onEdit(video)} className="gap-2" data-testid={`video-edit-btn-${video.video_id}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit Video
              </DropdownMenuItem>
              {video.dsp_url && (
                <DropdownMenuItem asChild className="gap-2">
                  <a href={video.dsp_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3.5 h-3.5" /> Open Source
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDelete(video)}
                className="gap-2 text-red-400 focus:text-red-400"
                data-testid={`video-delete-btn-${video.video_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Stats */}
        <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {video.plays || 0} views</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {video.likes_count || 0}</span>
          {video.linked_track_title && (
            <span className="truncate">Track: {video.linked_track_title}</span>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─── Video Manager (Tab Content) ────────────────────────── */
export default function VideoManager({ videos, onRefresh, getAuthHeaders, tracks, artistProfile, user, subscription }) {
  const [editingVideo, setEditingVideo] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);

  const handleDelete = async () => {
    if (!deletingVideo) return;
    try {
      const res = await fetch(`${API}/videos/${deletingVideo.video_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Video deleted');
        onRefresh?.();
      } else {
        toast.error('Failed to delete video');
      }
    } catch {
      toast.error('Failed to delete video');
    }
    setDeletingVideo(null);
  };

  return (
    <div className="space-y-6" data-testid="video-manager">
      {/* Video Grid */}
      <Card className="glass-card border-border p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold uppercase flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-400" />
            My Videos ({videos.length})
          </h2>
        </div>

        {videos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map(v => (
              <VideoCard
                key={v.video_id}
                video={v}
                onEdit={setEditingVideo}
                onDelete={setDeletingVideo}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Video className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No videos yet</p>
            <p className="text-xs mt-1">Upload your first video using the upload section above</p>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      {editingVideo && (
        <VideoEditModal
          video={editingVideo}
          open={!!editingVideo}
          onClose={() => setEditingVideo(null)}
          onSaved={onRefresh}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Delete Confirmation */}
      {deletingVideo && (
        <Dialog open={!!deletingVideo} onOpenChange={() => setDeletingVideo(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="video-delete-confirm">
            <DialogHeader>
              <DialogTitle className="text-red-400 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Delete Video
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete "<strong className="text-foreground">{deletingVideo.title}</strong>"? This cannot be undone.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeletingVideo(null)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="video-delete-confirm-btn">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
